// File: app/src/utils/muscleVolume.js
//
// Weekly hard sets per muscle group, counted from completed sessions' plans.
// Feeds the "## Weekly Muscle Volume" prompt section and the What's next
// reason line (#83, L1).
//
// Why count at all: most muscles grow best with at least ~10 hard sets a week
// (ACSM 2026; Pelland 2024), and the history the generator saw before was only
// exercise names — it had no way to tell that back got 4 sets this week while
// chest got 16.
//
// Generated Work nodes carry no muscle metadata, so muscles come from the
// exercise LABEL via the exercise library. Deterministic, free, and it works
// on every session already in history. Indirect work counts half a set (the
// "fractional" method Pelland 2024 found best supported): a bench press is a
// chest set plus half a triceps set and half a shoulder set.

import { daysSince, sessionHappened } from "./trainingHistory";
import { exerciseOf, resolveExercise } from "../config/exercises";

export const MUSCLE_GROUPS = [
  "chest",
  "back",
  "shoulders",
  "quads",
  "hamstrings",
  "glutes",
  "biceps",
  "triceps",
  "calves",
  "core",
];

// The groups the ~10 sets/week guidance is about. Arms, calves and core get
// plenty of indirect work and are reported but never flagged as behind.
export const MAJOR_MUSCLE_GROUPS = [
  "chest",
  "back",
  "shoulders",
  "quads",
  "hamstrings",
  "glutes",
];

export const WEEKLY_SET_TARGET = 10;
// Past this, more direct work adds little this week (Pelland 2024).
export const WEEKLY_SET_CEILING = 20;

/**
 * { primary, secondary } muscle groups for an exercise label, from the
 * exercise library (config/exercises.js). Ergs, runs, mobility drills and
 * unknown labels train nothing here — they are not resistance volume.
 */
// Patterns that train muscles but are not hard resistance sets: jumps and
// throws are low-load and stopped well short of fatigue, and conditioning
// moves (burpees, battle ropes) are metabolic work.
const NON_VOLUME_PATTERNS = new Set([
  "jump",
  "throw",
  "conditioning",
  "locomotion",
  "mobility",
  "breathing",
]);

// Takes a plan step, or a bare name.
export const musclesForExercise = (step) => {
  const exercise =
    typeof step === "string" ? resolveExercise(step) : exerciseOf(step);
  if (!exercise || NON_VOLUME_PATTERNS.has(exercise.pattern)) {
    return { primary: [], secondary: [] };
  }
  return {
    primary: exercise?.primary || [],
    secondary: exercise?.secondary || [],
  };
};

// Rounds a time-capped Group (AMRAP) is credited with: about one round per
// 3 minutes. An EMOM window (60-240 s) therefore counts once, as it should.
const roundsForTimeCap = (node) => {
  const minutes = node.unit === "m" ? node.value : node.value / 60;
  return Math.max(1, Math.floor((Number(minutes) || 0) / 3));
};

const isWarmOrCool = (node) =>
  /warm|cool/i.test(node?.label || "") ||
  node?.type === "Warmup" ||
  node?.type === "Cooldown";

/**
 * Hard sets per muscle group in one plan tree.
 * @returns {Object<string, number>} muscle -> sets (fractional)
 */
export const muscleSetsForPlan = (plan) => {
  const totals = {};
  const add = (muscle, amount) => {
    totals[muscle] = (totals[muscle] || 0) + amount;
  };

  const walk = (node, multiplier) => {
    if (!node || typeof node !== "object" || isWarmOrCool(node)) return;
    if (node.type === "Group") {
      const value = Number(node.value) || 1;
      const next =
        node.unit === "sets" || node.unit === "rounds"
          ? multiplier * value
          : node.unit === "s" || node.unit === "m"
            ? multiplier * roundsForTimeCap(node)
            : multiplier;
      (node.children || []).forEach((child) => walk(child, next));
      return;
    }
    if (node.type !== "Work") return;
    // Calories and distances are cardio; reps and holds are resistance.
    if (node.unit !== "reps" && node.unit !== "s") return;
    const { primary, secondary } = musclesForExercise(node);
    primary.forEach((m) => add(m, multiplier));
    secondary.forEach((m) => add(m, multiplier * 0.5));
  };

  if (Array.isArray(plan?.children)) plan.children.forEach((c) => walk(c, 1));
  else walk(plan, 1);
  return totals;
};

/**
 * Hard sets per muscle group across completed sessions in the last 7 days.
 * @returns {Object<string, number>} every MUSCLE_GROUPS key, rounded to 0.5
 */
export const weeklyMuscleSets = (sessions, { now = Date.now() } = {}) => {
  const totals = Object.fromEntries(MUSCLE_GROUPS.map((m) => [m, 0]));
  for (const session of Array.isArray(sessions) ? sessions : []) {
    if (!sessionHappened(session) || !session.plan_data) continue;
    const days = daysSince(session.started_at, now);
    if (days == null || days >= 7) continue;
    const sets = muscleSetsForPlan(session.plan_data);
    for (const [muscle, n] of Object.entries(sets)) totals[muscle] += n;
  }
  for (const m of MUSCLE_GROUPS) totals[m] = Math.round(totals[m] * 2) / 2;
  return totals;
};

const titleCase = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const joinNames = (names) =>
  names.length <= 1
    ? names.join("")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/** Major muscle groups under the weekly target, most behind first. */
export const undertrainedMuscles = (muscleSets) =>
  MAJOR_MUSCLE_GROUPS.filter(
    (m) => (muscleSets?.[m] ?? 0) < WEEKLY_SET_TARGET,
  ).sort((a, b) => (muscleSets?.[a] ?? 0) - (muscleSets?.[b] ?? 0));

/** "Back and hamstrings are under 10 hard sets this week." or null. */
export const describeUndertrained = (muscleSets) => {
  const behind = undertrainedMuscles(muscleSets);
  if (!muscleSets || behind.length === 0) return null;
  const total = Object.values(muscleSets).reduce((a, b) => a + b, 0);
  if (total === 0) return null;
  const names = behind.slice(0, 3);
  const verb = names.length === 1 ? "is" : "are";
  return `${titleCase(joinNames(names))} ${verb} under ${WEEKLY_SET_TARGET} hard sets this week.`;
};

/**
 * The optional "## Weekly Muscle Volume" prompt block. "" when no resistance
 * work was logged this week — the session-level history already says that,
 * and a table of zeros only invites the model to cram everything in at once.
 */
export const buildMuscleVolumeSection = (muscleSets) => {
  if (!muscleSets) return "";
  const total = Object.values(muscleSets).reduce((a, b) => a + b, 0);
  if (total === 0) return "";

  const counts = MUSCLE_GROUPS.map(
    (m) => `${titleCase(m)} ${muscleSets[m] ?? 0}`,
  ).join(", ");
  const behind = undertrainedMuscles(muscleSets).map(titleCase);
  const over = MUSCLE_GROUPS.filter(
    (m) => (muscleSets[m] ?? 0) > WEEKLY_SET_CEILING,
  ).map(titleCase);

  const lines = [
    `Hard sets per muscle group from completed sessions in the last 7 days (indirect work counts half): ${counts}.`,
    `Most muscles grow best with at least ~${WEEKLY_SET_TARGET} hard sets a week.`,
  ];
  if (behind.length) {
    lines.push(
      `Under that this week: ${joinNames(behind)}. When today's styles include resistance work and the user's Target areas and session notes leave room, favour these.`,
    );
  }
  if (over.length) {
    lines.push(
      `Already above ~${WEEKLY_SET_CEILING} sets this week: ${joinNames(over)} — no extra direct work for these today.`,
    );
  }
  lines.push(
    "Explicit Target areas, Avoid lists and session notes always win over this.",
  );

  return `
## Weekly Muscle Volume
${lines.join("\n")}
`;
};
